import { useState, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useWODraws, useNextDrawNumber } from "@/hooks/useWODraws";
import { createBillApproval } from "@/lib/createBillApproval";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtQty = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

interface Props {
  wo: any;
  woLines: any[];
  sovLines: any[];
  projectId: string;
  project?: any;
  onDrawGenerated: () => void;
}

export const WODrawGrid = ({ wo, woLines, sovLines, projectId, project, onDrawGenerated }: Props) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const { data: draws, refetch: refetchDraws } = useWODraws(wo.id);
  const nextDrawNumber = useNextDrawNumber(wo.id);

  // Only unit-level lines
  const unitLines = woLines.filter((wol: any) => wol.unit_id);

  // Unique units
  const woUnits = useMemo(() => {
    const unitMap = new Map<string, { id: string; number: string }>();
    unitLines.forEach((wol: any) => {
      if (wol.unit_id && wol.unit_schedule) {
        unitMap.set(wol.unit_id, { id: wol.unit_id, number: wol.unit_schedule.unit_number });
      }
    });
    return Array.from(unitMap.values()).sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
  }, [unitLines]);

  // SOV lines for this WO
  const woSovLines = useMemo(() => {
    const slIds = new Set(unitLines.map((wol: any) => wol.sov_line_id));
    return (sovLines ?? []).filter((sl: any) => slIds.has(sl.id));
  }, [unitLines, sovLines]);

  // Lookup: unitId+sovLineId -> woLine
  const lineMap = useMemo(() => {
    const m = new Map<string, any>();
    unitLines.forEach((wol: any) => {
      m.set(`${wol.unit_id}::${wol.sov_line_id}`, wol);
    });
    return m;
  }, [unitLines]);

  const getLine = (unitId: string, sovLineId: string) => lineMap.get(`${unitId}::${sovLineId}`);

  const toggleLine = (wolId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(wolId)) next.delete(wolId);
      else next.add(wolId);
      return next;
    });
  };

  const selectAllComplete = () => {
    const ids = unitLines
      .filter((wol: any) => wol.status === "complete")
      .map((wol: any) => wol.id);
    setSelectedIds(new Set(ids));
  };

  // Summary by SOV line
  const billSummary = useMemo(() => {
    const groups: Record<string, { sovLine: any; count: number; totalQty: number; totalValue: number; unitNumbers: string[] }> = {};
    selectedIds.forEach(wolId => {
      const wol = unitLines.find((l: any) => l.id === wolId);
      if (!wol) return;
      const sl = woSovLines.find((s: any) => s.id === wol.sov_line_id);
      if (!sl) return;
      if (!groups[wol.sov_line_id]) {
        groups[wol.sov_line_id] = { sovLine: sl, count: 0, totalQty: 0, totalValue: 0, unitNumbers: [] };
      }
      groups[wol.sov_line_id].count++;
      groups[wol.sov_line_id].totalQty += Number(wol.quantity);
      groups[wol.sov_line_id].totalValue += Number(wol.quantity) * Number(wol.contract_rate);
      const unitNum = wol.unit_schedule?.unit_number ?? "?";
      groups[wol.sov_line_id].unitNumbers.push(unitNum);
    });
    return Object.values(groups);
  }, [selectedIds, unitLines, woSovLines]);

  const subtotal = billSummary.reduce((s, g) => s + g.totalValue, 0);
  const retainagePct = Number(wo.retainage_pct ?? 10);
  const retainageAmt = subtotal * (retainagePct / 100);
  const netDue = subtotal - retainageAmt;

  // Cumulative retainage from previous draws
  const cumulativeRetainage = useMemo(() => {
    return (draws ?? []).reduce((s: number, d: any) => s + Number(d.retainage_amount ?? 0), 0) + retainageAmt;
  }, [draws, retainageAmt]);

  const handleGenerateDraw = async () => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      const userId = (await supabase.auth.getUser()).data.user?.id;

      // Create draw header
      const { data: draw, error: drawErr } = await supabase.from("wo_draws" as any).insert({
        company_id: companyId,
        work_order_id: wo.id,
        draw_number: nextDrawNumber,
        draw_date: new Date().toISOString().split("T")[0],
        status: "draft",
        subtotal,
        retainage_pct: retainagePct,
        retainage_amount: retainageAmt,
        net_due: netDue,
        total_retainage_held: cumulativeRetainage,
        notes: `Draw #${nextDrawNumber} — ${selectedIds.size} lines`,
        created_by: userId,
      }).select("id").single();

      if (drawErr) throw drawErr;

      // Create draw lines
      const drawLines = Array.from(selectedIds).map(wolId => {
        const wol = unitLines.find((l: any) => l.id === wolId)!;
        return {
          company_id: companyId,
          draw_id: (draw as any).id,
          work_order_line_id: wolId,
          unit_id: wol.unit_id,
          sov_line_id: wol.sov_line_id,
          description: `${wol.description} — Unit ${wol.unit_schedule?.unit_number ?? "?"}`,
          quantity: Number(wol.quantity),
          unit_of_measure: wol.unit ?? "",
          contract_rate: Number(wol.contract_rate),
          line_total: Number(wol.quantity) * Number(wol.contract_rate),
        };
      });

      const { error: dlErr } = await supabase.from("wo_draw_lines" as any).insert(drawLines);
      if (dlErr) throw dlErr;

      // Update WO lines status to "billed" and set billed_amount + draw_id
      for (const wolId of selectedIds) {
        const wol = unitLines.find((l: any) => l.id === wolId)!;
        await supabase.from("work_order_lines").update({
          status: "billed",
          billed_amount: Number(wol.quantity) * Number(wol.contract_rate),
          draw_id: (draw as any).id,
        } as any).eq("id", wolId);
      }

      // Create approval request so draw appears in Approval Queue
      await createBillApproval({
        companyId,
        requestType: "draw_approve",
        title: `Draw #${nextDrawNumber} — ${wo.number}`,
        projectId,
        linkedWoId: wo.id,
        linkedDrawId: (draw as any).id,
        requestData: { subtotal, retainage: retainageAmt, net_due: netDue, lines_count: selectedIds.size },
        createdBy: userId,
      });

      // Audit log
      await supabase.from("ops_audit_log").insert({
        table_name: "wo_draws",
        record_id: (draw as any).id,
        action: "draw_generated",
        new_status: "draft",
        changed_by: userId,
        metadata: {
          wo_number: wo.number,
          draw_number: nextDrawNumber,
          lines_count: selectedIds.size,
          subtotal,
          retainage: retainageAmt,
          net_due: netDue,
        },
      } as any);

      toast.success(`Draw #${nextDrawNumber} generated — ${selectedIds.size} lines | Net: ${fmt(netDue)}`);
      setSelectedIds(new Set());
      refetchDraws();
      onDrawGenerated();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (woUnits.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No unit-level lines on this WO.</p>;
  }

  // Cell status symbols
  const getCellSymbol = (wol: any) => {
    if (wol.status === "paid") return { symbol: "★", label: "paid", color: "text-amber-500" };
    if (wol.status === "billed") return { symbol: "●", label: "billed", color: "text-muted-foreground" };
    if (wol.status === "complete") return { symbol: "◕", label: "complete", color: "text-emerald-600" };
    return { symbol: "○", label: "pending", color: "text-muted-foreground/50" };
  };

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">Draw #{nextDrawNumber} — {wo.number}</span>
          <Button variant="outline" size="sm" onClick={selectAllComplete}>Select All Complete</Button>
          <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="text-amber-500">★</span> paid</span>
            <span className="flex items-center gap-1"><span>●</span> billed</span>
            <span className="flex items-center gap-1"><span className="text-emerald-600">◕</span> complete</span>
            <span className="flex items-center gap-1"><span className="opacity-50">○</span> pending</span>
          </div>
        </div>

        <ScrollArea className="max-h-[400px]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 bg-background">
                <tr>
                  <th className="border p-1.5 text-left text-xs font-medium text-muted-foreground sticky left-0 bg-background min-w-[80px]">Unit</th>
                  {woSovLines.map((sl: any) => (
                    <th key={sl.id} className="border p-1.5 text-center text-xs font-medium min-w-[100px]">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <div className="font-mono text-[10px] text-muted-foreground">{sl.item_code ?? `#${sl.line_number}`}</div>
                            <div className="truncate max-w-[90px]">{sl.description?.split(" ")[0]}</div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent><p>{sl.description}</p></TooltipContent>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {woUnits.map(unit => (
                  <tr key={unit.id}>
                    <td className="border p-1.5 font-mono text-xs font-medium sticky left-0 bg-background">
                      {unit.number}
                    </td>
                    {woSovLines.map((sl: any) => {
                      const wol = getLine(unit.id, sl.id);

                      if (!wol) {
                        return <td key={sl.id} className="border p-1 text-center text-muted-foreground/30">—</td>;
                      }

                      const qty = Number(wol.quantity);
                      const rate = Number(wol.contract_rate);
                      const { symbol, label, color } = getCellSymbol(wol);
                      const isComplete = wol.status === "complete";
                      const isLocked = wol.status === "billed" || wol.status === "paid";
                      const isSelected = selectedIds.has(wol.id);

                      if (isLocked) {
                        return (
                          <td key={sl.id} className="border p-1 text-center bg-muted/30">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={cn("text-xs cursor-default", color)}>{symbol} {label}</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{fmtQty(qty)} {wol.unit ?? ""} · {fmt(qty * rate)}</p>
                              </TooltipContent>
                            </Tooltip>
                          </td>
                        );
                      }

                      if (!isComplete) {
                        return (
                          <td key={sl.id} className="border p-1 text-center">
                            <span className="text-xs text-muted-foreground/50">
                              ○ {fmtQty(qty)}
                            </span>
                          </td>
                        );
                      }

                      // Complete — checkable for draw
                      return (
                        <td
                          key={sl.id}
                          className={cn(
                            "border p-1 text-center cursor-pointer transition-colors",
                            isSelected ? "bg-primary/10" : "hover:bg-accent/30"
                          )}
                          onClick={() => toggleLine(wol.id)}
                        >
                          <div className="flex items-center justify-center gap-1">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleLine(wol.id)}
                            />
                            <span className="text-xs">{fmtQty(qty)} {wol.unit ?? ""}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ScrollArea>

        {/* Draw Summary */}
        {billSummary.length > 0 && (
          <div className="border rounded-md p-3 space-y-1 bg-muted/30 text-sm">
            <div className="text-xs font-semibold text-muted-foreground mb-1">DRAW #{nextDrawNumber} SUMMARY</div>
            {billSummary.map(g => (
              <div key={g.sovLine.id} className="flex justify-between">
                <span>
                  <span className="font-mono text-xs text-muted-foreground mr-1">{g.sovLine.item_code ?? `#${g.sovLine.line_number}`}</span>
                  {g.sovLine.description} · {g.unitNumbers.length > 3 ? `${g.unitNumbers.length} units` : `Unit${g.unitNumbers.length > 1 ? "s" : ""} ${g.unitNumbers.join(", ")}`}
                  {" · "}{fmtQty(g.totalQty)} {g.sovLine.unit_of_measure ?? ""}
                </span>
                <span className="font-medium">= {fmt(g.totalValue)}</span>
              </div>
            ))}
            <div className="border-t pt-1 space-y-0.5">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-medium">{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Retainage ({retainagePct}%)</span>
                <span>-{fmt(retainageAmt)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-1">
                <span>Net due Draw #{nextDrawNumber}</span>
                <span>{fmt(netDue)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground pt-1">
                <span>Cumulative retainage held (all draws)</span>
                <span>{fmt(cumulativeRetainage)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button onClick={handleGenerateDraw} disabled={busy || selectedIds.size === 0}>
            {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Generating…</> : `Approve & Generate Draw #${nextDrawNumber} →`}
          </Button>
        </div>
      </div>
    </TooltipProvider>
  );
};
